/**
 * AgentPanelHeader Component
 * Header component for the Agent Panel
 */

import React from 'react'
import { Settings, Minimize2, Maximize2 } from 'lucide-react'

interface AgentPanelHeaderProps {
  /**
   * Current agent name to display
   */
  agentName?: string
  /**
   * Current agent status
   */
  agentStatus?: 'ready' | 'thinking' | 'offline'
  /**
   * Whether the panel is expanded
   */
  isExpanded?: boolean
  /**
   * Callback to toggle expanded state
   */
  onToggleExpanded?: () => void
  /**
   * Callback when settings is clicked
   */
  onSettingsClick?: () => void
}

/**
 * Header component showing Templatean logo, agent status, and controls
 */
export const AgentPanelHeader: React.FC<AgentPanelHeaderProps> = ({
  agentName,
  agentStatus = 'ready',
  isExpanded = false,
  onToggleExpanded,
  onSettingsClick,
}) => {
  const statusColors = {
    ready: 'bg-green-500',
    thinking: 'bg-amber-500 animate-pulse',
    offline: 'bg-gray-400',
  }

  const statusText = {
    ready: 'Ready',
    thinking: 'Thinking...',
    offline: 'Offline',
  }

  return (
    <header className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
      {/* Logo and Brand */}
      <div className="flex items-center gap-2">
        <span className="text-lg font-bold text-blue-600">Templatean</span>
        <span className="text-xs text-gray-400">AI</span>
      </div>

      {/* Agent Status */}
      {agentName && (
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${statusColors[agentStatus]}`}
            title={statusText[agentStatus]}
          />
          <span className="text-sm font-medium text-gray-700">{agentName}</span>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onSettingsClick}
          className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          title="Settings"
          aria-label="Settings"
        >
          <Settings className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onToggleExpanded}
          className="rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          title={isExpanded ? 'Minimize' : 'Expand'}
          aria-label={isExpanded ? 'Minimize panel' : 'Expand panel'}
        >
          {isExpanded ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </header>
  )
}

export default AgentPanelHeader
